"use strict";

/**
 * Each field definition can have:
 * - input: "text" | "textArea" | "select" | "measurement" | "yesNo" | "date" | "months" | "count" |
 *          "year" | "float" | "floatHundredth" | "readOnly"
 * - label: Display label for the input
 * - name: (optional) Overrides the definition key when mapping to inventory data
 * - options: (optional) Array or function returning select options
 * - unitOptions: (optional) Array or function returning unit options for measurement inputs
 * - defaultUnits: (optional) Unit selected while the asset has none stored
 * - visibleWhen: (optional) `{ fields, matches }` hiding the input until `matches` passes for the
 *   whole inventory record. `fields` lists the inventory data names the rule reads, which is what
 *   the page watches to know when to render itself again.
 * - isDisabled: (optional) Boolean or function returning whether the input is read only
 * - required: (optional) Boolean or function returning whether the field has to be filled in
 * - requiredWhen: (optional) `{ fields, matches }` in the same shape as `visibleWhen`, for fields
 *   whose required-ness varies with the rest of the record
 * - derivedValue: (optional) `{ fields, get }` for a value that follows from the rest of the record
 * - unselectedMessage: (optional) Placeholder shown by a select while nothing is selected
 * - allowUnselect: (optional) Lets a select return to its placeholder
 * - showNotFoundSelected: (optional) Whether a select shows values missing from its options
 * - units: (optional) Suffix appended to a readOnly value
 * - format: (optional) Converts a readOnly value into the string to display
 * - singleFile: (optional) Limits a dropzone to one file instead of any number of files
 * - valueClassName: (optional) Extra class on a readOnly value, for code that replaces the value
 *   in place after the page is rendered
 */
const HardAssetInventoryFields = function () {
  const getFieldDefinitions = function (assetType) {
    const domain = (table) => () => ToolSettings.getDomainOptions(table);
    const muniDropdown = (key) => () => FormSettings.getDropdownOptions(key, { tool: "muni" });
    const lengthUnits = () => OutfallInspectionConstants.lengthUnitOptions;
    const elevationUnits = () => OutfallInspectionConstants.elevationUnitOptions;
    const volumeUnits = () => BmpFcsInventoryConstants.volumeUnitOptions;
    const crossSectionUnits = {
      pipe: "inches",
      culvert: "feet",
      openChannel: "feet",
      manhole: "inches",
      cleanout: "inches",
      liftStation: "feet",
    }[assetType];
    const requiredFor = (...assetTypes) => assetTypes.includes(assetType);
    const whenFcs = (extraCondition = null) => ({
      visibleWhen: {
        fields: ["fcs", ...(extraCondition?.fields ?? [])],
        matches: (data) => data.fcs === true && (extraCondition?.matches(data) ?? true),
      },
    });
    // The structural BMP type decides which of its type specific fields are shown and required
    const byBmpType = (fieldKey) => ({
      visibleWhen: {
        fields: ["bmpType"],
        matches: (data) => bmpTypeShowsField(data.bmpType, fieldKey),
      },
      requiredWhen: {
        fields: ["bmpType"],
        matches: (data) => bmpTypeRequiresField(data.bmpType, fieldKey),
      },
    });

    return {
      // Shared summary fields
      landOwnership: {
        input: "select",
        label: "Land Ownership",
        options: muniDropdown("land-ownership"),
        // Responsible-party selects start unset rather than defaulting to the first party
        unselectedMessage: "Select an Owner",
        allowUnselect: true,
      },

      responsiblePartyDelivery: {
        input: "select",
        label: "Delivered By",
        options: muniDropdown("implementer-type"),
        unselectedMessage: "Select a Party",
        allowUnselect: true,
      },

      responsiblePartyMaintenance: {
        input: "select",
        label: "Maintained By",
        options: muniDropdown("maintainer-type"),
        unselectedMessage: "Select a Party",
        allowUnselect: true,
      },

      confinedSpace: { input: "yesNo", label: "Confined Space" },
      accessible: { input: "yesNo", label: "Accessible" },

      // Asset type fields, each stored as the asset's `type`
      pipeType: {
        name: "type",
        input: "select",
        label: "Pipe Type",
        options: domain("pipe_type"),
        required: true,
      },
      culvertType: {
        name: "type",
        input: "select",
        label: "Culvert Type",
        options: domain("culvert_type"),
        required: true,
      },
      openChannelType: {
        name: "type",
        input: "select",
        label: "Open Channel Type",
        options: domain("channel_type"),
        required: true,
      },
      manholeType: {
        name: "type",
        input: "select",
        label: "Manhole Type",
        options: domain("manhole_type"),
        required: true,
      },
      cleanoutType: {
        name: "type",
        input: "select",
        label: "Cleanout Type",
        options: domain("cleanout_type"),
        required: true,
      },
      liftStationType: {
        name: "type",
        input: "select",
        label: "Lift Station Type",
        options: domain("lift_station_type"),
        required: true,
      },
      outfallType: {
        input: "select",
        label: "Outfall Type",
        options: muniDropdown("outfall-type"),
      },

      // Asset phase
      phase: {
        input: "select",
        label: "Asset Phase",
        options: () => getEnabledPhaseOptions(OutfallInventoryConstants.phaseOptions),
        isDisabled: () => HardAssetAssociatedProject.isPhaseLockedByProject(),
        required: true,
      },

      // Structural BMPs record the design phase as `planned` while every other asset type uses `design`
      bmpPhase: {
        name: "phase",
        input: "select",
        label: "Asset Phase",
        options: () => getEnabledPhaseOptions(BmpFcsInventoryConstants.phaseOptions),
        isDisabled: () => HardAssetAssociatedProject.isPhaseLockedByProject(),
        required: true,
      },

      installDate: {
        input: "date",
        label: "Install Date",
        // Assets only have an install date once they have reached construction
        visibleWhen: {
          fields: ["phase"],
          matches: (data) => HardAssetInventoryConfig.phaseHasInstallDate(data.phase),
        },
        required: true,
      },

      // Line assets
      material: {
        input: "select",
        label: "Asset Material",
        options: domain("material"),
        required: requiredFor("pipe", "culvert", "openChannel"),
      },
      lineServiceType: {
        name: "serviceType",
        input: "select",
        label: "Line Service Type",
        options: domain("line_service"),
        required: requiredFor("pipe", "culvert"),
      },
      lineShape: {
        name: "shape",
        input: "select",
        label: "Asset Shape",
        options: domain("line_shape"),
        required: requiredFor("pipe", "culvert"),
      },
      flowType: {
        input: "select",
        label: "Flow Type",
        options: domain("flow_type"),
        required: true,
      },
      flowDirection: {
        name: "flowDirectionText",
        input: "select",
        label: "Flow Direction",
        options: domain("flow_direction"),
      },

      lineDiameter: {
        name: "diameter",
        input: "measurement",
        label: "Diameter",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
        visibleWhen: { fields: ["shape"], matches: (data) => data.shape === "circular" },
        required: true,
      },
      lineWidth: {
        name: "width",
        input: "measurement",
        label: "Width",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
        visibleWhen: { fields: ["shape"], matches: (data) => data.shape !== "circular" },
        required: true,
      },
      // The cross-section dimension, as opposed to the linear length along the asset
      lineLength: {
        name: "length",
        input: "measurement",
        label: "Height",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
        visibleWhen: { fields: ["shape"], matches: (data) => data.shape !== "circular" },
        required: true,
      },
      designDepth: {
        name: "depth",
        input: "measurement",
        label: "Design Depth",
        unitOptions: lengthUnits,
        defaultUnits: "feet",
      },
      linearFeet: { input: "readOnly", label: "Linear Length", units: " ft" },
      slope: { input: "float", label: "Slope" },
      wall: { input: "yesNo", label: "Wall" },
      channelArea: { name: "area", input: "float", label: "Area (sq-ft)" },

      // Open channels have no diameter, so their cross-section dimensions apply to every shape
      channelWidth: {
        name: "width",
        input: "measurement",
        label: "Width",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
        required: true,
      },
      channelHeight: {
        name: "length",
        input: "measurement",
        label: "Height",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
      },

      lined: { input: "yesNo", label: "Lined" },
      linerMethod: {
        input: "select",
        label: "Lining Method",
        options: domain("lining_method"),
        visibleWhen: { fields: ["lined"], matches: (data) => data.lined === true },
      },
      linerYear: {
        input: "year",
        label: "Lining Year",
        visibleWhen: { fields: ["lined"], matches: (data) => data.lined === true },
      },

      upstreamElevation: { input: "float", label: "Upstream Elevation (ft)" },
      downstreamElevation: { input: "float", label: "Downstream Elevation (ft)" },

      // Junction structures
      frameMaterial: { input: "select", label: "Frame Material", options: domain("material") },
      wallMaterial: { input: "select", label: "Wall Material", options: domain("material") },
      surfaceMaterial: { input: "select", label: "Surface Material", options: domain("material") },
      coverType: {
        input: "select",
        label: "Cover Type",
        options: domain("manhole_cover_type"),
      },
      coneType: { input: "select", label: "Cone Type", options: domain("manhole_cone_type") },
      coverShape: {
        input: "select",
        label: "Cover Shape",
        options: domain("manhole_cover_shape"),
      },
      junctionDiameter: {
        name: "diameter",
        input: "measurement",
        label: "Diameter",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
        visibleWhen: { fields: ["coverShape"], matches: (data) => data.coverShape === "circular" },
      },
      junctionWidth: {
        name: "width",
        input: "measurement",
        label: "Width",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
        visibleWhen: { fields: ["coverShape"], matches: (data) => data.coverShape !== "circular" },
      },
      junctionLength: {
        name: "length",
        input: "measurement",
        label: "Length",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
        visibleWhen: { fields: ["coverShape"], matches: (data) => data.coverShape !== "circular" },
      },
      junctionDepth: {
        name: "depth",
        input: "measurement",
        label: "Junction Depth",
        unitOptions: lengthUnits,
        defaultUnits: "feet",
      },

      // Lift stations
      serviceType: { input: "select", label: "Service Type", options: domain("line_service") },
      width: {
        input: "measurement",
        label: "Width",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
      },
      length: {
        input: "measurement",
        label: "Length",
        unitOptions: lengthUnits,
        defaultUnits: crossSectionUnits,
      },
      totalDepth: {
        name: "depth",
        input: "measurement",
        label: "Total Depth",
        unitOptions: lengthUnits,
        defaultUnits: "inches",
      },
      sump: { input: "yesNo", label: "Sump" },
      sumpDepth: {
        input: "measurement",
        label: "Sump Depth",
        unitOptions: lengthUnits,
        defaultUnits: "inches",
        visibleWhen: { fields: ["sump"], matches: (data) => data.sump === true },
      },
      designVolume: {
        input: "measurement",
        label: "Design Volume",
        unitOptions: volumeUnits,
        defaultUnits: "cubicFeet",
      },
      storageVolume: {
        input: "measurement",
        label: "Storage Volume",
        unitOptions: volumeUnits,
        defaultUnits: "cubicFeet",
      },
      drainageArea: {
        name: "area",
        input: "float",
        label: "Drainage Area (acres)",
        required: true,
      },

      // Outfall outlets. Outlet shapes come from a configurable dropdown, so which of them read as
      // circular is a setting rather than a single code like the domain-backed shapes other assets use.
      outletMaterial: {
        input: "select",
        label: "Asset Material",
        options: muniDropdown("outlet-material"),
      },
      outletOtherMaterial: {
        input: "text",
        label: "Other Material",
        visibleWhen: {
          fields: ["outletMaterial"],
          matches: (data) => data.outletMaterial === "other",
        },
      },
      outletType: { input: "select", label: "Outlet Type", options: muniDropdown("outlet-type") },
      outletShape: {
        input: "select",
        label: "Asset Shape",
        options: muniDropdown("outlet-shape"),
        required: true,
      },
      outletOtherShape: {
        input: "text",
        label: "Other Shape",
        visibleWhen: { fields: ["outletShape"], matches: (data) => data.outletShape === "other" },
      },
      outletDiameter: {
        input: "measurement",
        label: "Diameter",
        unitOptions: lengthUnits,
        defaultUnits: "inches",
        visibleWhen: {
          fields: ["outletShape"],
          matches: (data) => outletShapeIsCircular(data.outletShape),
        },
        required: true,
      },
      outletWidth: {
        input: "measurement",
        label: "Width",
        unitOptions: lengthUnits,
        defaultUnits: "inches",
        visibleWhen: {
          fields: ["outletShape"],
          matches: (data) => !outletShapeIsCircular(data.outletShape),
        },
        required: true,
      },
      // The cross-section dimension, as opposed to the linear length along the asset
      outletLength: {
        input: "measurement",
        label: "Height",
        unitOptions: lengthUnits,
        defaultUnits: "inches",
        visibleWhen: {
          fields: ["outletShape"],
          matches: (data) => !outletShapeIsCircular(data.outletShape),
        },
        required: true,
      },
      outletInvertElevation: {
        input: "measurement",
        label: "Invert Elevation",
        unitOptions: elevationUnits,
        defaultUnits: "feet",
      },
      outletBaseElevation: {
        input: "measurement",
        label: "Base Elevation",
        unitOptions: elevationUnits,
        defaultUnits: "feet",
      },
      outletRimElevation: {
        input: "measurement",
        label: "Rim Elevation",
        unitOptions: elevationUnits,
        defaultUnits: "feet",
      },

      // Structural BMPs
      bmpType: {
        input: "select",
        label: "BMP Type",
        options: () => BmpFcsInventoryConstants.bmpTypeOptions,
        required: true,
      },
      catchBasinType: {
        name: "bmpType",
        input: "select",
        label: "Catch Basin Type",
        options: () => BmpFcsInventoryConstants.catchBasinTypeOptions,
        isDisabled: true,
        derivedValue: {
          fields: ["bmpType", "sump", "designDepth"],
          get: (data) => {
            const derivedType = data.sump === true && data.designDepth > 1 ? 11 : 12;

            return derivedType === parseInt(data.bmpType) ? undefined : derivedType;
          },
        },
      },
      inProximityTo303dWaterbody: {
        input: "yesNo",
        label: "In Proximity to 303(d) Waterbody",
      },
      fcs: {
        input: "yesNo",
        label: "Full Capture System",
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => bmpTypeAllowsFcs(data.bmpType),
        },
        derivedValue: {
          fields: ["bmpType", "fcs"],
          get: (data) => (bmpTypeAllowsFcs(data.bmpType) || data.fcs === false ? undefined : false),
        },
      },

      // Full Capture System section
      trapsParticles: {
        input: "yesNo",
        label: "Traps Particles >5MM",
        required: true,
        ...whenFcs(),
      },
      treatsStorm: {
        input: "yesNo",
        label: "Treats 1YR 1HR Storm",
        required: true,
        ...whenFcs(),
      },
      stormInches: {
        input: "float",
        label: "1YR 1HR Storm (in/hr)",
        ...whenFcs({ fields: ["treatsStorm"], matches: (data) => data.treatsStorm === true }),
      },
      partialStormInches: {
        input: "float",
        label: "Treated Storm (in/hr)",
        ...whenFcs({ fields: ["treatsStorm"], matches: (data) => data.treatsStorm === false }),
      },
      fcsInspectionFrequencyMonths: {
        input: "months",
        label: "Trash FCS Inspection (months)",
        required: true,
        ...whenFcs(),
      },
      fcsSummaryFiles: {
        input: "dropzone",
        label: "FCS Justification Summary",
        singleFile: true,
        ...whenFcs(),
      },
      multiSystem: {
        input: "radio",
        label: "FCS Grouping",
        options: () => BmpFcsInventoryConstants.multiSystemOptions,
        required: true,
        ...whenFcs(),
      },
      volumeBased: {
        input: "radio",
        label: "FCS Function",
        options: () => BmpFcsInventoryConstants.volumeBasedOptions,
        required: true,
        ...whenFcs(),
      },
      stateApproved: {
        input: "yesNo",
        label: "On State-Approved List",
        ...whenFcs({ fields: ["multiSystem"], matches: (data) => data.multiSystem === false }),
      },
      fcsOmPlanFiles: {
        input: "dropzone",
        label: "O&M Plan",
        singleFile: true,
        ...whenFcs({ fields: ["multiSystem"], matches: (data) => data.multiSystem === true }),
      },
      fcsDesignPlanFiles: {
        input: "dropzone",
        label: "Design Plan",
        singleFile: true,
        ...whenFcs({ fields: ["multiSystem"], matches: (data) => data.multiSystem === true }),
      },
      treatmentTrain: {
        input: "yesNo",
        label: "Treatment Train",
        visibleWhen: {
          fields: ["bmpType", "tt", "centralizedBmps"],
          matches: (data) => canJoinTreatmentTrain(data),
        },
      },
      // Drainage area page
      totalDrainageArea: {
        name: "drainageArea",
        input: "readOnly",
        label: "Total area",
        units: " acres",
        format: NunjucksFilters.hundredths,
        valueClassName: "total-treated",
      },
      pluTreated: {
        input: "readOnly",
        label: "PLU area in drainage (acres)",
        units: " acres",
        format: NunjucksFilters.hundredths,
        valueClassName: "priority-treated",
      },
      imperviousArea: {
        input: "floatHundredth",
        label: "Impervious Area in Drainage (acres)",
        requiredWhen: {
          fields: ["bmpType"],
          matches: (data) => !isCatchBasinType(data.bmpType),
        },
      },
      flowLength: {
        input: "floatHundredth",
        label: "Maximum Flow Path Length (ft)",
        required: true,
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => BmpFcsFunctions.isCentralizedBMPType(data.bmpType),
        },
      },

      systemAccess: {
        input: "radio",
        label: "System Accessibility",
        options: () => BmpFcsInventoryConstants.systemAccessOptions,
        required: true,
      },
      visibleScreen: { input: "yesNo", label: "Visible Screen at Outlet", required: true },
      screenCount: {
        input: "count",
        label: "Number of Screens",
        visibleWhen: { fields: ["visibleScreen"], matches: (data) => data.visibleScreen === true },
      },
      treatedStormType: {
        input: "select",
        label: "Design Storm Type",
        options: () => BmpFcsInventoryConstants.treatedStormTypeOptions,
        required: true,
      },
      treatedStormPercentile: {
        input: "select",
        label: "Design Storm Percentile",
        options: () => BmpFcsInventoryConstants.treatedStormPercentileOptions,
        visibleWhen: {
          fields: ["treatedStormType"],
          matches: (data) => data.treatedStormType === "24-hr-percentile",
        },
        required: true,
      },
      treatedStormDepthsInches: {
        input: "float",
        label: "Design Storm Depth (in)",
        visibleWhen: {
          fields: ["treatedStormType"],
          matches: (data) => data.treatedStormType === "24-hr-depth",
        },
        required: true,
      },
      diversion: { input: "yesNo", label: "Dry Weather Diversion", required: true },
      lowFlow: { input: "yesNo", label: "Low Flow Bypass", required: true },
      drainsout: { input: "yesNo", label: "Capture & Reuse", required: true },
      intakeRate: {
        input: "float",
        label: "Intake Rate (cfs)",
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => !BmpFcsFunctions.isDecentralizedBMPType(data.bmpType),
        },
      },

      scenarioLowFlow: {
        name: "lowFlow",
        input: "yesNo",
        label: "Low Flow Bypass",
        required: true,
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => !BmpFcsFunctions.isDecentralizedBMPType(data.bmpType),
        },
      },
      scenarioDiversion: {
        name: "diversion",
        input: "yesNo",
        label: "Dry Weather Diversion",
        required: true,
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => !BmpFcsFunctions.isDecentralizedBMPType(data.bmpType),
        },
      },
      inletType: {
        input: "select",
        label: "Inlet Type",
        options: () => BmpFcsInventoryConstants.inletTypeOptions,
      },
      inlets: { input: "count", label: "Observable Inlets", required: true },
      outlets: { input: "count", label: "Observable Outlets", required: true },
      catchBasinShape: {
        name: "shape",
        input: "select",
        label: "Asset Shape",
        options: domain("catch_basin_shape"),
      },
      bmpDiameter: {
        name: "diameter",
        input: "float",
        label: "Diameter (ft)",
        visibleWhen: { fields: ["shape"], matches: (data) => data.shape === "circular" },
      },
      bmpWidth: {
        name: "width",
        input: "float",
        label: "Width (ft)",
        visibleWhen: { fields: ["shape"], matches: (data) => data.shape !== "circular" },
      },
      bmpLength: {
        name: "length",
        input: "float",
        label: "Length (ft)",
        visibleWhen: { fields: ["shape"], matches: (data) => data.shape !== "circular" },
      },
      bmpTotalDepth: { name: "totalDepth", input: "float", label: "Total Depth (ft)" },
      cleanout: { input: "yesNo", label: "Cleanout" },
      bmpSumpDepth: {
        name: "designDepth",
        input: "float",
        label: "Sump Depth (ft)",
        visibleWhen: { fields: ["sump"], matches: (data) => data.sump === true },
      },
      siphon: { input: "yesNo", label: "Siphon" },
      footprintMaterial: {
        input: "select",
        label: "Footprint Material",
        options: () => BmpFcsInventoryConstants.materialOptions,
        ...byBmpType("footprintMaterial"),
      },
      bmpWallMaterial: {
        name: "wallMaterial",
        input: "select",
        label: "Wall Material",
        options: () => BmpFcsInventoryConstants.materialOptions,
        ...byBmpType("bmpWallMaterial"),
      },
      treatmentCapacity: {
        input: "float",
        label: "Treatment Capacity (cu-ft)",
        ...byBmpType("treatmentCapacity"),
      },
      footprint: { input: "float", label: "Footprint (sq-ft)", ...byBmpType("footprint") },
      treatmentRate: {
        input: "float",
        label: "Treatment Rate (cfs)",
        ...byBmpType("treatmentRate"),
      },
      infiltrationRate: {
        input: "float",
        label: "Infiltration Rate (in/hr)",
        ...byBmpType("infiltrationRate"),
      },
      drawdownTime: {
        input: "float",
        label: "Drawdown Time (hrs)",
        ...byBmpType("drawdownTime"),
      },
      wetPoolCapacity: {
        input: "float",
        label: "Wet Pool Capacity (cu-ft)",
        ...byBmpType("wetPoolCapacity"),
      },
      manufacturer: { input: "text", label: "Manufacturer", ...byBmpType("manufacturer") },
      nameModel: { input: "text", label: "Name/Model", ...byBmpType("nameModel") },
      mediaType: { input: "text", label: "Media Type", ...byBmpType("mediaType") },
      filters: { input: "count", label: "Number of Filters", ...byBmpType("filters") },
      filterThickness: {
        input: "float",
        label: "Filter Thickness (ft)",
        ...byBmpType("filterThickness"),
      },

      // Closed space fields
      standingWater: {
        input: "yesNo",
        label: "Obs Port - Standing Water",
        visibleWhen: { fields: ["systemAccess"], matches: (data) => data.systemAccess !== true },
        required: true,
      },
      sedimentAccumulation: {
        input: "yesNo",
        label: "Obs Port - Accumulation",
        visibleWhen: { fields: ["systemAccess"], matches: (data) => data.systemAccess !== true },
        required: true,
      },
      maintenanceInterval: {
        input: "months",
        label: "Closed Space Maintenance Interval (months)",
        visibleWhen: { fields: ["systemAccess"], matches: (data) => data.systemAccess !== true },
        required: true,
      },
      bmpInspectFreq: {
        input: "months",
        label: "Functional Assessment (months)",
        required: true,
      },
      certificationInspectionFrequencyMonths: {
        input: "months",
        label: "Certification Inspection (months)",
      },
      bmpInvertElevation: {
        name: "invertElevation",
        input: "float",
        label: "Invert Elevation (ft)",
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => bmpTypeHasSpecificFields(data.bmpType),
        },
      },
      bmpBaseElevation: {
        name: "baseElevation",
        input: "float",
        label: "Base Elevation (ft)",
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => bmpTypeHasSpecificFields(data.bmpType),
        },
      },
      bmpRimElevation: {
        name: "rimElevation",
        input: "float",
        label: "Rim Elevation (ft)",
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => bmpTypeHasSpecificFields(data.bmpType),
        },
      },
      bmpDatum: {
        name: "datum",
        input: "select",
        label: "Elevation Datum",
        options: muniDropdown("datum"),
        visibleWhen: {
          fields: ["bmpType"],
          matches: (data) => bmpTypeHasSpecificFields(data.bmpType),
        },
      },

      // Elevations, always feet to match the ESWFT base unit
      invertElevation: { input: "float", label: "Invert Elevation (ft)" },
      baseElevation: { input: "float", label: "Base Elevation (ft)" },
      rimElevation: { input: "float", label: "Rim Elevation (ft)" },
      sumpElevation: { input: "float", label: "Sump Elevation (ft)" },
      outletElevation: { input: "float", label: "Outlet Elevation (ft)" },
      datum: { input: "select", label: "Elevation Datum", options: muniDropdown("datum") },
      elevationDatum: {
        input: "select",
        label: "Elevation Datum",
        options: muniDropdown("datum"),
      },

      // Inspection, monitoring & screening
      routineSiteVisitFrequencyMonths: { input: "months", label: "Routine Site Visit (months)" },
      wqMonitoringLocation: { input: "yesNo", label: "WQ Monitoring Location" },
      dryWeatherMonitoringLocation: { input: "yesNo", label: "Dry Weather Screening Location" },
      dryWeatherScreeningFrequencyMonths: {
        input: "months",
        label: "Screening Frequency (months)",
        visibleWhen: {
          fields: ["dryWeatherMonitoringLocation"],
          matches: (data) => data.dryWeatherMonitoringLocation === true,
        },
      },

      // Other information
      structureIdentifier: { input: "text", label: "Structure ID" },
      structureId: { input: "text", label: "Structure ID" },
      cofIndividualOverrideText: {
        input: "select",
        label: "User Defined Consequence of Failure",
        options: muniDropdown("consequence-of-failure"),
        unselectedMessage: "Calculated",
        allowUnselect: true,
        showNotFoundSelected: false,
      },
      notes: { input: "textArea", label: "Notes" },
      additionalFiles: { input: "dropzone", label: "Additional Files" },
    };
  };

  // A group is one grid of fields within a section
  const group = function (fields, { title = null, columns = 5 } = {}) {
    return { title, columns, fields };
  };

  const gridClassNamesByColumnCount = {
    2: "grid-two-columns",
    3: "grid-three-columns",
    4: "grid-four-columns",
    5: "grid-five-columns-minmax",
  };

  // The shared helper reads a missing shape as circular, which suits inspections but would leave an
  // outfall with no shape recorded showing only a diameter. Here an unset shape shows width and height.
  const outletShapeIsCircular = function (outletShape) {
    return !!outletShape && OutfallInspectionConstants.outletShapeIsCircular(outletShape);
  };

  const summaryFields = [
    "landOwnership",
    "responsiblePartyDelivery",
    "responsiblePartyMaintenance",
    "confinedSpace",
  ];
  const lineAttributeFields = ["material", "lineServiceType", "slope", "flowDirection"];
  const lineDimensionFields = ["lineShape", "lineDiameter", "lineWidth", "lineLength"];
  const liningGroup = group(["lined", "linerMethod", "linerYear"], {
    title: "Lining",
    columns: 3,
  });
  const lineElevationGroup = group(["upstreamElevation", "downstreamElevation", "elevationDatum"], {
    title: "Elevations",
    columns: 3,
  });
  const junctionStructureGroups = [
    group(["frameMaterial", "wallMaterial", "surfaceMaterial", "coverType", "coneType"]),
    group(["coverShape", "junctionDiameter", "junctionWidth", "junctionLength", "junctionDepth"], {
      title: "Dimensions",
      columns: 4,
    }),
  ];
  const fcsGroups = [
    group(
      [
        "fcs",
        "trapsParticles",
        "treatsStorm",
        "stormInches",
        "partialStormInches",
        "fcsInspectionFrequencyMonths",
      ],
      { columns: 3 },
    ),
    group(["fcsSummaryFiles"], { columns: 2 }),
    group(["multiSystem", "volumeBased"], { title: "Type Information", columns: 2 }),
    group(["stateApproved", "fcsOmPlanFiles", "fcsDesignPlanFiles"], {
      title: "Type Specific Information",
      columns: 2,
    }),
  ];
  const bmpPhaseGroups = [group(["bmpPhase", "installDate"])];
  const bmpMonitoringFields = [
    "routineSiteVisitFrequencyMonths",
    "bmpInspectFreq",
    "certificationInspectionFrequencyMonths",
    "dryWeatherMonitoringLocation",
    "dryWeatherScreeningFrequencyMonths",
  ];
  const bmpMonitoringGroups = [group(bmpMonitoringFields, { columns: 4 })];
  const structuralBmpMonitoringGroups = [
    group([...bmpMonitoringFields, "maintenanceInterval"], { columns: 4 }),
  ];

  const sharedSections = {
    summary: [group(summaryFields)],
    phase: [group(["phase", "installDate"])],
    monitoring: [
      group(
        [
          "routineSiteVisitFrequencyMonths",
          "dryWeatherMonitoringLocation",
          "dryWeatherScreeningFrequencyMonths",
        ],
        { columns: 4 },
      ),
    ],
    otherInformation: [
      group(["structureIdentifier", "cofIndividualOverrideText"], { columns: 4 }),
      group(["notes", "additionalFiles"], { columns: 2 }),
    ],
  };

  const assetTypeSections = {
    pipe: {
      summary: [group(["pipeType", ...summaryFields, "accessible"])],
      specific: [
        group(lineAttributeFields),
        group([...lineDimensionFields, "linearFeet"], { title: "Dimensions", columns: 4 }),
        liningGroup,
        lineElevationGroup,
      ],
    },

    culvert: {
      summary: [group(["culvertType", ...summaryFields, "accessible"])],
      specific: [
        group([...lineAttributeFields, "wall"]),
        group([...lineDimensionFields, "designDepth", "linearFeet"], {
          title: "Dimensions",
          columns: 4,
        }),
        liningGroup,
        lineElevationGroup,
      ],
    },

    openChannel: {
      summary: [group(["openChannelType", ...summaryFields, "accessible"])],
      specific: [
        group(["material", "lineServiceType", "flowType", "slope", "flowDirection"]),
        group(
          [
            "lineShape",
            "channelWidth",
            "channelHeight",
            "designDepth",
            "channelArea",
            "linearFeet",
          ],
          { title: "Dimensions", columns: 4 },
        ),
        liningGroup,
        lineElevationGroup,
      ],
    },

    manhole: {
      summary: [group(["manholeType", ...summaryFields])],
      specific: [
        ...junctionStructureGroups,
        group(["invertElevation", "baseElevation", "rimElevation", "datum"], {
          title: "Elevations",
          columns: 4,
        }),
      ],
    },

    cleanout: {
      summary: [group(["cleanoutType", ...summaryFields])],
      specific: [
        ...junctionStructureGroups,
        group(["invertElevation", "baseElevation", "rimElevation", "elevationDatum"], {
          title: "Elevations",
          columns: 4,
        }),
      ],
    },

    liftStation: {
      summary: [group(["liftStationType", ...summaryFields])],
      specific: [
        group(["material", "serviceType", "drainageArea"]),
        group(
          ["length", "width", "totalDepth", "sump", "sumpDepth", "designVolume", "storageVolume"],
          { title: "Dimensions & Capacity", columns: 4 },
        ),
        group(
          [
            "invertElevation",
            "baseElevation",
            "rimElevation",
            "sumpElevation",
            "outletElevation",
            "elevationDatum",
          ],
          { title: "Elevations", columns: 4 },
        ),
      ],
    },

    outfall: {
      summary: [group(["outfallType", ...summaryFields])],
      monitoring: [
        group(
          [
            "routineSiteVisitFrequencyMonths",
            "wqMonitoringLocation",
            "dryWeatherMonitoringLocation",
            "dryWeatherScreeningFrequencyMonths",
          ],
          { columns: 4 },
        ),
      ],
      otherInformation: [
        group(["structureId", "cofIndividualOverrideText"], { columns: 4 }),
        group(["notes", "additionalFiles"], { columns: 2 }),
      ],
      specific: [
        group(["outletMaterial", "outletOtherMaterial", "outletType"]),
        group(
          ["outletShape", "outletOtherShape", "outletDiameter", "outletWidth", "outletLength"],
          { title: "Dimensions", columns: 4 },
        ),
        group(["outletInvertElevation", "outletBaseElevation", "outletRimElevation", "datum"], {
          title: "Elevations",
          columns: 4,
        }),
      ],
    },

    bmpFcs: {
      summary: [group(["bmpType", ...summaryFields, "inProximityTo303dWaterbody"])],
      phase: bmpPhaseGroups,
      monitoring: structuralBmpMonitoringGroups,
      fcs: fcsGroups,
      specific: [
        group([
          "treatmentTrain",
          "inlets",
          "outlets",
          "visibleScreen",
          "screenCount",
          "treatedStormType",
          "treatedStormDepthsInches",
          "treatedStormPercentile",
          "manufacturer",
          "nameModel",
          "filters",
          "mediaType",
          "filterThickness",
          "footprintMaterial",
          "bmpWallMaterial",
        ]),
        group(["diversion", "lowFlow", "drainsout", "intakeRate"], {
          title: "Flow Control",
          columns: 4,
        }),
        group(
          [
            "bmpTotalDepth",
            "treatmentCapacity",
            "footprint",
            "wetPoolCapacity",
            "infiltrationRate",
            "treatmentRate",
            "drawdownTime",
          ],
          { title: "Treatment & Sizing", columns: 4 },
        ),
        group(["systemAccess", "standingWater", "sedimentAccumulation"], {
          title: "System Accessibility",
          columns: 3,
        }),
        group(["bmpInvertElevation", "bmpBaseElevation", "bmpRimElevation", "bmpDatum"], {
          title: "Elevations",
          columns: 4,
        }),
      ],
    },

    scenario: {
      summary: [group(["bmpType"])],
      phase: bmpPhaseGroups,
      monitoring: [],
      otherInformation: [],
      specific: [
        group(
          [
            "treatmentCapacity",
            "footprint",
            "treatmentRate",
            "infiltrationRate",
            "drawdownTime",
            "wetPoolCapacity",
            "scenarioLowFlow",
            "scenarioDiversion",
            "drainsout",
            "intakeRate",
          ],
          { columns: 4 },
        ),
      ],
    },

    // Catch basins share the bmpFcs model and asset type, but have their own field set
    catchBasin: {
      summary: [group(["catchBasinType", ...summaryFields, "inProximityTo303dWaterbody"])],
      phase: bmpPhaseGroups,
      monitoring: bmpMonitoringGroups,
      fcs: fcsGroups,
      specific: [
        group([
          "inletType",
          "siphon",
          "cleanout",
          "footprintMaterial",
          "bmpWallMaterial",
          "manufacturer",
          "nameModel",
        ]),
        group(
          [
            "catchBasinShape",
            "bmpDiameter",
            "bmpWidth",
            "bmpLength",
            "bmpTotalDepth",
            "sump",
            "bmpSumpDepth",
          ],
          { title: "Dimensions", columns: 4 },
        ),
        group(["invertElevation", "baseElevation", "rimElevation", "datum"], {
          title: "Elevations",
          columns: 4,
        }),
      ],
    },
  };

  const bmpTypeSpecificFields = {
    // Dry Basin
    1: {
      required: ["treatmentCapacity", "footprint", "infiltrationRate", "drawdownTime"],
      optional: ["nameModel", "footprintMaterial", "bmpWallMaterial"],
    },
    // Wet Basin
    2: {
      required: ["treatmentCapacity", "footprint", "drawdownTime", "wetPoolCapacity"],
      optional: ["nameModel", "footprintMaterial", "bmpWallMaterial"],
    },
    // Infiltration Basin
    3: {
      required: ["treatmentCapacity", "footprint", "infiltrationRate"],
      optional: ["nameModel", "footprintMaterial", "bmpWallMaterial"],
    },
    // Treatment Vault
    4: {
      required: ["treatmentCapacity", "footprint", "treatmentRate"],
      optional: ["manufacturer", "nameModel", "footprintMaterial", "bmpWallMaterial"],
    },
    // Media Filter
    5: {
      required: ["treatmentCapacity", "footprint", "treatmentRate"],
      optional: [
        "manufacturer",
        "nameModel",
        "mediaType",
        "filters",
        "filterThickness",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Bed Filter
    6: {
      required: ["treatmentCapacity", "footprint", "treatmentRate"],
      optional: [
        "nameModel",
        "mediaType",
        "filters",
        "filterThickness",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Settling Basin
    7: {
      required: ["treatmentCapacity", "footprint"],
      optional: ["nameModel", "footprintMaterial", "bmpWallMaterial"],
    },
    // Biofiltration
    8: {
      required: [],
      optional: [
        "treatmentCapacity",
        "footprint",
        "manufacturer",
        "nameModel",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Infiltration Feature
    9: {
      required: [],
      optional: [
        "treatmentCapacity",
        "footprint",
        "manufacturer",
        "nameModel",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Pervious Pavement
    10: {
      required: [],
      optional: [
        "treatmentCapacity",
        "footprint",
        "nameModel",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Sediment Trap
    11: { required: [], optional: [] },
    // Drop Inlet
    12: { required: [], optional: [] },
    // Bioretention
    13: {
      required: [],
      optional: [
        "treatmentCapacity",
        "footprint",
        "manufacturer",
        "nameModel",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Detention Basin
    14: {
      required: ["treatmentCapacity", "footprint", "drawdownTime"],
      optional: ["nameModel", "footprintMaterial", "bmpWallMaterial"],
    },
    // Bioswale
    15: {
      required: [],
      optional: [
        "treatmentCapacity",
        "footprint",
        "nameModel",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Unknown
    16: { required: [], optional: [] },
    // Filtration Device
    17: {
      required: [],
      optional: [
        "treatmentCapacity",
        "manufacturer",
        "nameModel",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Trash Trap
    18: {
      required: [],
      optional: [
        "treatmentCapacity",
        "manufacturer",
        "nameModel",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Retention Basin
    19: {
      required: ["treatmentCapacity", "footprint", "wetPoolCapacity"],
      optional: ["nameModel", "footprintMaterial", "bmpWallMaterial"],
    },
    // Green Roof
    20: {
      required: [],
      optional: ["treatmentCapacity", "footprint", "footprintMaterial", "bmpWallMaterial"],
    },
    // Rain Barrel
    21: {
      required: [],
      optional: [
        "treatmentCapacity",
        "footprint",
        "manufacturer",
        "nameModel",
        "footprintMaterial",
        "bmpWallMaterial",
      ],
    },
    // Cistern
    22: {
      required: ["treatmentCapacity", "footprint"],
      optional: ["manufacturer", "nameModel", "footprintMaterial", "bmpWallMaterial"],
    },
  };

  const getEnabledPhaseOptions = function (phaseOptions) {
    if (ToolSettings.getSetting("construction", "enable-post-active-phase")) {
      return phaseOptions;
    }

    return phaseOptions.filter((option) => option.value !== "post-active");
  };

  // The drainage area page holds too few fields to group into sections, so it lists them in one row
  const bmpDrainageFields = ["totalDrainageArea", "pluTreated", "imperviousArea", "flowLength"];
  const drainageFieldsByLayout = {
    bmpFcs: bmpDrainageFields,
    catchBasin: bmpDrainageFields,
    scenario: ["totalDrainageArea", "imperviousArea", "flowLength"],
  };

  var getDrainageFields = function (assetType, data) {
    return resolveFields(
      drainageFieldsByLayout[getLayoutKey(assetType, data)] ?? [],
      getFieldDefinitions(assetType),
      data,
    );
  };

  var getSections = function (assetType, data) {
    return resolveLayoutSections(assetType, data).filter(hasVisibleFields);
  };

  var resolveLayoutSections = function (assetType, data) {
    const layout = { ...sharedSections, ...assetTypeSections[getLayoutKey(assetType, data)] };
    const definitions = getFieldDefinitions(assetType);

    const sections = [
      { className: "asset-summary", groups: layout.summary },
      { title: "Asset Phase", groups: layout.phase },
      { title: "Design Characteristics", groups: layout.specific },
      { title: "Full Capture System Information", groups: layout.fcs ?? [] },
      { title: "Inspection, Monitoring & Screening Information", groups: layout.monitoring },
      { title: "Other Information", groups: layout.otherInformation },
    ];

    return sections.map((section) => resolveSection(section, definitions, data));
  };

  // A section every field of which is hidden, like the FCS section of an asset that is not one,
  // has nothing left to show
  var hasVisibleFields = function (section) {
    return section.groups.some((group) => !group.hidden);
  };

  var getLayoutKey = function (assetType, data) {
    if (assetType !== "bmpFcs") {
      return assetType;
    }
    if (isCatchBasinType(data.bmpType)) {
      return "catchBasin";
    }
    if (Tree.get("dataView") === "scenarios") {
      return "scenario";
    }
    return assetType;
  };

  var isCatchBasinType = function (bmpType) {
    return (
      bmpType !== null && bmpType !== undefined && BmpFcsFunctions.isPriorityCatchBasinType(bmpType)
    );
  };

  // The catch basin layout is not type dependent, so it keeps its own copy of these fields shown
  var bmpTypeShowsField = function (bmpType, fieldKey) {
    if (isCatchBasinType(bmpType)) {
      return true;
    }

    const typeFields = bmpTypeSpecificFields[bmpType];

    return (
      !!typeFields &&
      (typeFields.required.includes(fieldKey) || typeFields.optional.includes(fieldKey))
    );
  };

  var bmpTypeRequiresField = function (bmpType, fieldKey) {
    return bmpTypeSpecificFields[bmpType]?.required.includes(fieldKey) ?? false;
  };

  var bmpTypeHasSpecificFields = function (bmpType) {
    if (isCatchBasinType(bmpType)) {
      return true;
    }

    const typeFields = bmpTypeSpecificFields[bmpType];

    return !!typeFields && typeFields.required.length + typeFields.optional.length > 0;
  };

  var bmpTypeAllowsFcs = function (bmpType) {
    if (bmpType === null || bmpType === undefined) {
      return true;
    }

    return (
      !BmpFcsFunctions.isBmpOnlyType(bmpType) &&
      !FormConstants.unknownOnlyTypeNumbers.includes(parseInt(bmpType))
    );
  };

  var canJoinTreatmentTrain = function (data) {
    if (
      !BmpFcsFunctions.isCentralizedBMPType(data.bmpType) ||
      BmpFcsInventoryConstants.nonTreatmentTrainsTypes.includes(parseInt(data.bmpType))
    ) {
      return false;
    }

    return getUnpairedCentralizedBmps(data).length > 0 || !!data.tt;
  };

  var getUnpairedCentralizedBmps = function (data) {
    return getOtherCentralizedBmps(data).filter((bmp) => !bmp.tt);
  };

  var getOtherCentralizedBmps = function (data) {
    return (data.centralizedBmps ?? []).filter((bmp) => bmp.idbmp !== data.id);
  };

  var getTreatmentTrainRow = function (data) {
    if (!data.treatmentTrain) {
      return null;
    }

    const otherCentralizedBmps = getOtherCentralizedBmps(data);

    return {
      assetName: data.name,
      flowDirection: data.tt ?? "1",
      flowDirectionOptions: BmpFcsInventoryConstants.treatmentTrainOptions,
      partnerName: getUnambiguousPartnerName(otherCentralizedBmps, data.id),
      partner: data.ttFlowsTo,
      partnerOptions: otherCentralizedBmps.map((bmp) => ({ name: bmp.bmp_id, value: bmp.idbmp })),
    };
  };

  var getUnambiguousPartnerName = function (otherCentralizedBmps, assetId) {
    const pairedCount = otherCentralizedBmps.filter((bmp) => bmp.tt !== null).length;

    if (otherCentralizedBmps.length === 1) {
      return otherCentralizedBmps[0].bmp_id;
    }
    if (pairedCount === 2) {
      return otherCentralizedBmps.find((bmp) => bmp.tt === null)?.bmp_id ?? null;
    }
    if (pairedCount === 3) {
      return otherCentralizedBmps.find((bmp) => bmp.ttFlowsTo === assetId)?.bmp_id ?? null;
    }

    return null;
  };

  var resolveSection = function (section, definitions, data) {
    return {
      ...section,
      groups: section.groups.map((group) => resolveGroup(group, definitions, data)),
    };
  };

  var resolveGroup = function (group, definitions, data) {
    const fields = resolveFields(group.fields, definitions, data);

    return {
      title: group.title,
      gridClassName: gridClassNamesByColumnCount[group.columns],
      fields,
      hidden: !fields.some((field) => !field.hidden),
    };
  };

  var resolveFields = function (fieldKeys, definitions, data) {
    return fieldKeys
      .map((key) => resolveField(definitions[key], key, data))
      .filter((field) => field !== null);
  };

  // Only fields the API returned for this asset are rendered, so the sections can safely list every field
  var resolveField = function (definition, key, data) {
    const name = definition.name ?? key;

    if (!(name in data)) {
      return null;
    }

    const isMeasurement = definition.input === "measurement";

    return {
      ...definition,
      name,
      value: definition.format ? definition.format(data[name]) : data[name],
      isDisabled: resolveConfiguredValue(definition.isDisabled),
      required: isRequired(definition, data),
      unitsName: isMeasurement ? `${name}Units` : null,
      unitsValue: isMeasurement ? (data[`${name}Units`] ?? definition.defaultUnits ?? null) : null,
      options: resolveConfiguredValue(definition.options) ?? null,
      unitOptions: resolveConfiguredValue(definition.unitOptions) ?? null,
      hidden: !isVisible(definition, data),
    };
  };

  var resolveConfiguredValue = function (value) {
    return typeof value === "function" ? value() : value;
  };

  var isVisible = function (definition, data) {
    return !definition.visibleWhen || definition.visibleWhen.matches(data);
  };

  var isRequired = function (definition, data) {
    if (definition.requiredWhen) {
      return definition.requiredWhen.matches(data);
    }

    return resolveConfiguredValue(definition.required) ?? false;
  };

  var getMissingRequiredFields = function (assetType, data) {
    const pageFields = [
      ...getAllFields(getSections(assetType, data)),
      ...getDrainageFields(assetType, data),
    ];

    return pageFields.filter((field) => field.required && !field.hidden && isEmpty(field.value));
  };

  var isEmpty = function (value) {
    return value === null || value === undefined || value === "";
  };

  var getWatchedFieldNames = function (sections) {
    const watchedFields = getAllFields(sections).flatMap((field) => [
      ...(field.visibleWhen?.fields ?? []),
      ...(field.requiredWhen?.fields ?? []),
      ...(field.derivedValue?.fields ?? []),
    ]);

    return [...new Set(watchedFields)];
  };

  var getAllFields = function (sections) {
    return sections.flatMap((section) => section.groups.flatMap((group) => group.fields));
  };

  var getConditionSignature = function (sections) {
    return JSON.stringify(
      sections.map((section) => ({
        title: section.title,
        groups: section.groups.map((group) => ({
          hidden: group.hidden,
          fields: group.fields.map((field) => [
            field.name,
            field.hidden,
            field.required,
            field.isDisabled,
          ]),
        })),
      })),
    );
  };

  var getFileFields = function (sections) {
    return getAllFields(sections).filter((field) => field.input === "dropzone");
  };

  var getDerivedUpdates = function (assetType, data, changedFieldName) {
    const rules = getAllFields(resolveLayoutSections(assetType, data)).filter((field) =>
      field.derivedValue?.fields.includes(changedFieldName),
    );

    const updates = {};
    for (const field of rules) {
      const derivedValue = field.derivedValue.get(data);
      if (derivedValue !== undefined) {
        updates[field.name] = derivedValue;
      }
    }

    return updates;
  };

  return {
    getSections,
    getDrainageFields,
    getTreatmentTrainRow,
    getMissingRequiredFields,
    getWatchedFieldNames,
    getConditionSignature,
    getDerivedUpdates,
    getFileFields,
    getUnpairedCentralizedBmps,
  };
};

module.exports = HardAssetInventoryFields();

const BmpFcsFunctions = require("../../bmpfcs/bmpFcsFunctions");
const BmpFcsInventoryConstants = require("../../bmpfcs/bmpFcsInventoryConstants");
const FormConstants = require("../../mapFunctions/formConstants");
const FormSettings = require("../../settings/formSettings");
const HardAssetAssociatedProject = require("./hardAssetAssociatedProject");
const HardAssetInventoryConfig = require("./hardAssetInventoryConfig");
const NunjucksFilters = require("../../general/nunjucksFilters");
const OutfallInspectionConstants = require("../../outfalls/outfallInspectionConstants");
const OutfallInventoryConstants = require("../../outfalls/outfallInventoryConstants");
const ToolSettings = require("../../settings/toolSettings");
const Tree = require("../../tree");
